import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Star, TrendingUp, Award, Users } from "lucide-react";

function rupee(n) { return "₹ " + Number(n || 0).toLocaleString("en-IN"); }

export default function KPI() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/kpi/summary").then(r => setData(r.data)); }, []);

  if (!data) return <div className="text-slate-500">Loading KPI scores…</div>;

  const maxDept = Math.max(1, ...data.by_department.map(d => d.avg_rating));

  return (
    <div className="space-y-6" data-testid="kpi-root">
      <div>
        <div className="text-xs uppercase font-semibold tracking-[0.1em] text-slate-500">KPI Scores</div>
        <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-slate-900">Who's winning at Techtreno.</h1>
        <p className="mt-1 text-sm text-slate-500">Aggregated ratings across all finalized performance cycles.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Finalized reviews" value={data.total_finalized} icon={Award} />
        <Stat label="Company avg rating" value={data.avg_rating_overall.toFixed(2) + " / 5"} icon={Star} tone="orange" />
        <Stat label="Avg hike delivered" value={data.avg_hike_overall.toFixed(1) + " %"} icon={TrendingUp} tone="green" />
        <Stat label="Departments rated" value={data.by_department.length} icon={Users} />
      </div>

      <Card className="p-6 rounded-xl border-slate-200/60">
        <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">Department leaderboard</h3>
        {data.by_department.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">No finalized reviews yet.</div>
        ) : (
          <div className="space-y-3">
            {data.by_department.map((d, i) => (
              <div key={d.department} className="flex items-center gap-4" data-testid={`dept-kpi-${d.department}`}>
                <div className="w-8 text-center">
                  <span className="text-lg font-display font-bold text-slate-400">#{i + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-semibold text-slate-900">{d.department}</span>
                    <span className="text-xs text-slate-500">{d.review_count} review{d.review_count !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Progress value={(d.avg_rating / maxDept) * 100} className="h-2 flex-1" />
                    <span className="text-sm font-bold text-[#DC2626] tabular-nums w-14 text-right">{d.avg_rating.toFixed(2)}/5</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Tabs defaultValue="ratings">
        <TabsList>
          <TabsTrigger value="ratings" data-testid="tab-ratings">Top by rating</TabsTrigger>
          <TabsTrigger value="hikes" data-testid="tab-hikes">Top by hike delivered</TabsTrigger>
        </TabsList>
        <TabsContent value="ratings">
          <PeopleTable rows={data.leaderboard} column="avg_rating" columnLabel="Avg rating" fmt={(v) => `${v.toFixed(2)}/5`} />
        </TabsContent>
        <TabsContent value="hikes">
          <PeopleTable rows={data.top_hikes} column="total_hike" columnLabel="Total hike" fmt={(v) => `${v.toFixed(1)}%`} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone = "red" }) {
  const tones = { red: "text-[#DC2626] bg-red-50", orange: "text-orange-600 bg-orange-50", green: "text-emerald-600 bg-emerald-50" };
  return (
    <Card className="p-5 rounded-xl border-slate-200/60">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">{label}</div>
          <div className="mt-1 font-display text-2xl font-bold text-slate-900">{value}</div>
        </div>
        <div className={`h-10 w-10 rounded-lg grid place-items-center ${tones[tone]}`}><Icon className="h-5 w-5" strokeWidth={1.5} /></div>
      </div>
    </Card>
  );
}

function PeopleTable({ rows, column, columnLabel, fmt }) {
  if (!rows?.length) return <div className="text-sm text-slate-500 py-10 text-center border border-dashed rounded-xl">No data yet.</div>;
  return (
    <Card className="rounded-xl border-slate-200/60 overflow-hidden">
      <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-slate-100 text-[10px] uppercase font-semibold tracking-wider text-slate-500">
        <div className="col-span-1">Rank</div>
        <div className="col-span-4">Employee</div>
        <div className="col-span-3">Department</div>
        <div className="col-span-2">Reviews</div>
        <div className="col-span-2 text-right">{columnLabel}</div>
      </div>
      {rows.map((r, i) => (
        <div key={r.employee_id} className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-slate-50 items-center" data-testid={`kpi-emp-${r.employee_id}`}>
          <div className="col-span-1 font-display font-bold text-slate-400">#{i + 1}</div>
          <div className="col-span-4 text-sm font-semibold text-slate-900">{r.employee_name}</div>
          <div className="col-span-3 text-sm text-slate-600">{r.department}</div>
          <div className="col-span-2 text-sm text-slate-600">{r.reviews}</div>
          <div className="col-span-2 text-right font-bold text-[#DC2626] tabular-nums">{fmt(r[column])}</div>
        </div>
      ))}
    </Card>
  );
}
